import { computed } from 'vue';

import UtilsService from '@dh-io-otb/shared/src/services/utils.service';
import BookingService from '@dh-io-otb/shared/src/services/booking.service';

import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';

import { TestDriveMode } from '@dh-io-otb/shared/src/models/utilities/testdrive-mode.enum';
import { i18n } from '@dh-io-otb/shared/src/services/translation.service';

const useSummary = () => {
  const { getter, state } = useStore();

  const { informationSent, model, selectedPeriod, selectedDealer, selectedVehicle, testDriveMode } = state.otbcFlow;
  const selectedTransmission = computed(() => state.otbcFlow.selectedTransmission);
  const confirmedFuelType = computed(() => state.otbcFlow.confirmedFuelType);
  const confirmedMotorisation = computed(() => state.otbcFlow.confirmedMotorisation);
  const confirmedDriveType = computed(() => state.otbcFlow.confirmedDriveType);

  const dealerInformation = computed(() => selectedDealer);
  const personalDetails = computed(() => informationSent);

  const buildBookingVehicleSummary = () => selectedVehicle;

  const buildRequestVehicleSummary = () => {
    const confirmedMotorisationTransmission =
      confirmedMotorisation.value?.transmissions?.length === 1
        ? confirmedMotorisation.value.transmissions[0]
        : selectedTransmission.value ?? null;
    const selectedEnergyEfficiency = confirmedMotorisation.value?.energyEfficiencyClass
      ? confirmedMotorisation.value?.energyEfficiencyClass[confirmedMotorisationTransmission] ?? null
      : null;
    return UtilsService.cleanObject({
      co2Emission: confirmedMotorisation.value?.co2Emission ?? null,
      energyEfficiencyClass: selectedEnergyEfficiency,
      engine: confirmedMotorisation.value?.engine ?? null,
      fuelConsumption: confirmedMotorisation.value?.fuelConsumption ?? null,
      fuelType: confirmedMotorisation.value?.fuelType ?? confirmedFuelType.value ?? null,
      images: confirmedMotorisation.value?.images ?? model.images,
      modelName: model.name,
      name: confirmedMotorisation.value?.name ?? null,
      transmission: confirmedMotorisationTransmission ?? null,
      driveType: confirmedMotorisation.value?.driveType ?? confirmedDriveType.value,
      wheelBase: state.otbcFlow.confirmedWheelBase
        ? i18n.t(`shared.wheelBase.${state.otbcFlow.confirmedWheelBase.toLowerCase()}`)
        : '',
      weight: state.otbcFlow.confirmedWeight
        ? i18n.t(`shared.weightCategory.${state.otbcFlow.confirmedWeight.toLowerCase()}`)
        : '',
    });
  };

  const dateAndPlace = computed(() => {
    let label;
    switch (testDriveMode) {
      case TestDriveMode.BOOKING:
        label = BookingService.buildBookingTimeLabel(
          getter.localeCode,
          BookingService.convertToSchedulePreference(getter.localeCode, selectedPeriod.start, selectedPeriod.end),
          dealerInformation.value.computedName,
        );
        break;
      case TestDriveMode.REQUEST: {
        let requestLocation = dealerInformation.value.computedName;
        if (getter?.isTestDriveFromHomeEnabled) requestLocation = i18n.t('summary.timeAndPlacePickMeUp.tdfh');

        label = BookingService.buildRequestTimeLabel(getter.localeCode, selectedPeriod, requestLocation);
        break;
      }
      default:
        break;
    }
    return label;
  });

  const summaryVehicleInformation = computed(() => {
    const { testDriveMode } = state.otbcFlow;
    let vehicle;
    switch (testDriveMode) {
      case TestDriveMode.BOOKING:
        vehicle = buildBookingVehicleSummary();
        break;
      case TestDriveMode.REQUEST:
        vehicle = buildRequestVehicleSummary();
        break;
      default:
        break;
    }
    return vehicle;
  });

  return {
    buildBookingVehicleSummary,
    buildRequestVehicleSummary,
    dateAndPlace,
    dealerInformation,
    personalDetails,
    summaryVehicleInformation,
  };
};

export { useSummary };
